import React, { useEffect, useState } from "react";
import axios from "../api/axiosInstance";

import {
  Paper,
  Typography,
  Box,
  TextField,
  MenuItem,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  IconButton,
  Tooltip,
  Breadcrumbs,
  Link,
} from "@mui/material";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";

import { useNavigate } from "react-router-dom";
import { useClientInfo } from "../utils/commonImports";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { drawClientHeader } from "../utils/reportClientHeader";
import MyDatePicker from "../components/MyDatePicker";
import { addPdfFooter } from "../utils/reportFooter";

/* ===============================
   DATE FORMATTER
================================ */
const formatDate = (val) => {
  if (!val) return "";
  const d = new Date(val);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
};

const convertToISO = (val) => {
  if (!val) return "";
  const [dd, mm, yyyy] = val.split("/");
  return `${yyyy}-${mm}-${dd}`;
};

const toISODate = (date) => {
  if (!date) return "";
  return date.toISOString().split("T")[0];
};

/* ===============================
   COLUMNS (SINGLE SOURCE)
   ================================ */
const BOOKING_COLUMNS = [
  { key: "reporting_date", label: "Reporting Date" },
  { key: "booking_no", label: "Booking No" },
  { key: "customername", label: "Customer Name" },
  { key: "duty_type", label: "Duty Type" },
  { key: "vehicle_group", label: "Group" },
  { key: "vehicle_number", label: "Vehicle No" },
  { key: "driver_name", label: "Driver" },
  { key: "release_date", label: "Release Date" },
  { key: "usage_days", label: "Days" },
  { key: "advance_amount", label: "Advance" },
  { key: "payment_mode", label: "Payment Mode" },
  { key: "booking_status", label: "Status" },
  { key: "tripsheet_number", label: "Tripsheet No" }, // ⭐ AFTER STATUS
];

export default function BookingReport() {
  const navigate = useNavigate();
  const { clientId, client } = useClientInfo();

  const [rows, setRows] = useState([]);

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [status, setStatus] = useState("");
  const [dutyType, setDutyType] = useState("");
  const [search, setSearch] = useState("");

  /* ===============================
     BACK BUTTON
     Goes to the previous page. If there is no history
     (page opened directly), goes to the Reports dashboard.
     ================================ */
  const handleBack = () => {
    if (window.history.state && window.history.state.idx > 0) {
      navigate(-1);
    } else {
      navigate("/reports");
    }
  };

  /* ===============================
     LOAD REPORT
     ================================ */
  const loadReport = async () => {
    const res = await axios.get("/api/bookings/report", {
      params: {
        client_id: clientId,
        from_date: toISODate(fromDate),
        to_date: toISODate(toDate),
        status,
        duty_type: dutyType,
        search,
      },
    });

    setRows(res.data || []);
  };

  useEffect(() => {
    if (clientId) loadReport();
  }, [clientId]);

  /* ===============================
     PDF GENERATOR
     ================================ */
  const generatePDF = () => {
    const doc = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a4",
    });

    const headerEndY = drawClientHeader(doc, client);

    doc.setFontSize(13);
    doc.setFont(undefined, "bold");
    doc.text("Bookings Report", 14, headerEndY);

    const columns = BOOKING_COLUMNS.map((c) => c.label);

    const body = rows.map((r) =>
      BOOKING_COLUMNS.map((c) => {
        if (c.key === "reporting_date" || c.key === "release_date") {
          return formatDate(r[c.key]);
        }
        return r[c.key];
      }),
    );

    autoTable(doc, {
      head: [columns],
      body,
      startY: headerEndY + 6,
      theme: "grid",
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: {
        fillColor: [230, 230, 230],
        textColor: [0, 0, 0],
        fontStyle: "bold",
      },
    });

    // =========================
    // FOOTER (ALWAYS LAST ❗)
    // =========================
    addPdfFooter(doc);

    return doc;
  };

  /* ===============================
     PRINT / PDF / EXCEL
     ================================ */
  const handlePrint = () => {
    const doc = generatePDF();
    const blob = doc.output("blob");
    const url = URL.createObjectURL(blob);
    const iframe = document.createElement("iframe");
    iframe.style.display = "none";
    iframe.src = url;
    document.body.appendChild(iframe);
    iframe.onload = () => iframe.contentWindow.print();
  };

  const handlePDF = () => {
    generatePDF().save("Bookings_Report.pdf");
  };

  const handleExcel = () => {
    const data = rows.map((r) => {
      const obj = {};
      BOOKING_COLUMNS.forEach((c) => {
        if (c.key === "reporting_date" || c.key === "release_date") {
          obj[c.label] = formatDate(r[c.key]);
        } else {
          obj[c.label] = r[c.key];
        }
      });
      return obj;
    });

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Bookings");
    XLSX.writeFile(wb, "Bookings_Report.xlsx");
  };

  /* ===============================
     UI
    ================================ */
  return (
    <Paper sx={{ p: 3 }}>
      {/* HEADER WITH BACK BUTTON */}
      <Box display="flex" alignItems="center" gap={1.5} mb={2}>
        <Tooltip title="Back">
          <IconButton
            onClick={handleBack}
            aria-label="Go back"
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              "&:hover": { background: "#e8f1ff", color: "#1a73e8" },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>

        <Box>
          <Breadcrumbs
            separator={<NavigateNextIcon fontSize="small" />}
            aria-label="breadcrumb"
          >
            <Link
              component="button"
              type="button"
              underline="hover"
              color="text.secondary"
              variant="body2"
              onClick={() => navigate("/reports")}
            >
              Reports
            </Link>
            <Typography variant="body2" color="text.primary" fontWeight={600}>
              Bookings
            </Typography>
          </Breadcrumbs>

          <Typography variant="h6" fontWeight={700}>
            Bookings Report
          </Typography>
        </Box>
      </Box>

      {/* FILTERS */}
      <Box display="flex" gap={1.5} mb={1.5} flexWrap="wrap">
        <MyDatePicker
          label="From Date"
          value={fromDate}
          onChange={setFromDate}
        />

        <MyDatePicker label="To Date" value={toDate} onChange={setToDate} />

        <TextField
          size="small"
          select
          label="Status"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          sx={{ minWidth: 110 }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="Pending">Pending</MenuItem>
          <MenuItem value="Confirmed">Confirmed</MenuItem>
          <MenuItem value="Completed">Completed</MenuItem>
          <MenuItem value="Cancelled">Cancelled</MenuItem>
        </TextField>

        <TextField
          size="small"
          select
          label="Duty Type"
          value={dutyType}
          onChange={(e) => setDutyType(e.target.value)}
          sx={{ minWidth: 160 }}
          InputProps={{ sx: { fontSize: 14 } }}
          InputLabelProps={{ sx: { fontSize: 14 } }}
        >
          <MenuItem value="">All</MenuItem>

          <MenuItem value="Local">Local</MenuItem>
          <MenuItem value="Outstation">Outstation</MenuItem>
          <MenuItem value="EmployeeTransfer">Employee Transfer</MenuItem>
          <MenuItem value="AirportTransfer">Airport Transfer</MenuItem>
          <MenuItem value="Contract">Monthly Contract</MenuItem>
          <MenuItem value="Package">Package</MenuItem>
          <MenuItem value="OSDayRate">OS Day Rate</MenuItem>
          <MenuItem value="KmSlab">Km Slab</MenuItem>
        </TextField>

        <TextField
          size="small"
          label="Search Booking / Customer / Vehicle / Driver"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 200 }}
        />

        <Button
          variant="contained"
          size="small"
          sx={{ height: 40, minWidth: 80 }}
          onClick={loadReport}
        >
          Search
        </Button>

        <Button
          variant="outlined"
          size="small"
          sx={{ height: 40, minWidth: 80 }}
          onClick={handlePrint}
        >
          Print
        </Button>

        <Button
          variant="outlined"
          size="small"
          color="error"
          sx={{ height: 40, minWidth: 80 }}
          onClick={handlePDF}
        >
          PDF
        </Button>

        <Button
          variant="outlined"
          size="small"
          color="success"
          sx={{ height: 40, minWidth: 80 }}
          onClick={handleExcel}
        >
          Excel
        </Button>
      </Box>

      {/* TABLE */}
      <TableContainer sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead sx={{ background: "#f1f5f9" }}>
            <TableRow>
              {BOOKING_COLUMNS.map((c) => (
                <TableCell key={c.key} sx={{ fontSize: 11, fontWeight: 700 }}>
                  {c.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.bookingid}>
                {BOOKING_COLUMNS.map((c) => (
                  <TableCell key={c.key} sx={{ fontSize: 11 }}>
                    {c.key === "reporting_date" || c.key === "release_date"
                      ? formatDate(r[c.key])
                      : r[c.key]}
                  </TableCell>
                ))}
              </TableRow>
            ))}

            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={BOOKING_COLUMNS.length} align="center">
                  No records found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}
